/**
 * Customer Service
 * API calls cho Customer module
 */

import api from "@/api/axios";
import type {
  IdentifyCustomerParams,
  IdentifyCustomerResponse,
} from "@/types/customer";
import {
  type MappedMembership,
  type RawMembershipResponse,
} from "@/types/user.types";

// ============================================
// API ENDPOINTS
// ============================================

const CUSTOMER_API_ENDPOINTS = {
  IDENTIFY: "/customers/identify",
} as const;

// ============================================
// SERVICE
// ============================================

export const customerService = {
  /**
   * Identify customer by email or QR code
   * GET /customers/identify
   */
  identifyCustomer: async (
    params: IdentifyCustomerParams,
  ): Promise<IdentifyCustomerResponse> => {
    const response = await api.get(CUSTOMER_API_ENDPOINTS.IDENTIFY, { params });
    return response.data?.data;
  },

  /**
   * Get customer membership information
   * GET /customers/profile/membership
   */
  getMembership: async (): Promise<MappedMembership> => {
    const response = await api.get<RawMembershipResponse>(
      "/customers/profile/membership",
    );
    const rawData = response.data.data;

    return {
      user: {
        id: rawData.customer.id,
        fullName: rawData.customer.full_name,
        email: rawData.customer.email,
        profileImageUrl: rawData.customer.profile_image_url,
        upgradeSpend: rawData.customer.upgrade_spend,
        maintenanceSpend: rawData.customer.maintenance_spend,
        lastMembershipUpdateDate: rawData.customer.last_membership_update_date,
        currentLevel: rawData.customer.level
          ? {
              id: rawData.customer.level.id,
              name: rawData.customer.level.name,
              rankPriority: rawData.customer.level.rank_priority,
              targetSpending: rawData.customer.level.target_spending,
              earningRate: rawData.customer.level.earning_rate,
              birthdayBonusPoints: rawData.customer.level.birthday_bonus_points,
              benefits: rawData.customer.level.benefits,
              icon: rawData.customer.level.icon,
            }
          : null,
      },
      levels: (rawData.levels || []).map((l: any) => ({
        id: l.id,
        name: l.name,
        rankPriority: l.rank_priority,
        targetSpending: l.target_spending,
        earningRate: l.earning_rate,
        birthdayBonusPoints: l.birthday_bonus_points,
        benefits: l.benefits,
        icon: l.icon,
      })),
      cycleDays: rawData.cycle_days,
      nextMembershipUpdateDate: rawData.next_membership_update_date,
    };
  },

  deleteProfile: async (): Promise<{
    message: string;
  }> => {
    const response = await api.delete<{ message: string }>(
      "/customers/profile/me",
    );
    return response.data;
  },

  /**
   * Export customer data
   * @param format 'json' | 'csv'
   */
  exportProfile: async (format: "json" | "csv"): Promise<void> => {
    const response = await api.get("/profile/export", {
      params: { format },
      responseType: "blob",
    });

    let filename = `MyLoyaltyData.${format}`;
    const disposition = response.headers["content-disposition"];
    if (disposition && disposition.indexOf("filename=") !== -1) {
      const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(
        disposition,
      );
      if (matches != null && matches[1]) {
        filename = matches[1].replace(/['"]/g, "");
      }
    }

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();

    if (link.parentNode) {
      link.parentNode.removeChild(link);
    }
    window.URL.revokeObjectURL(url);
  },
};
